import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, RefreshCw, Plus, ChevronLeft, ChevronRight, ArrowRight, Lock } from 'lucide-react';
import { API_BASE, useAuth } from '../../context/AuthContext';
import { formatDate } from '../../lib/admin-utils';
import { useAdmin } from '../../context/AdminContext';
import type { FilterMode, LinkItem, RedirectType, SortMode } from '../../types/admin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface PaginatedLinksResponse {
  links: LinkItem[];
  pagination?: {
    limit?: number;
    hasMore?: boolean;
    nextCursor?: string | null;
    search?: string | null;
    sort?: 'code' | 'recent' | 'oldest';
    redirectType?: 'all' | '301' | '302';
  };
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 280;

const selectClassName = 'rounded-md border border-input bg-background px-3 py-2 text-sm';

function localDateTimeToIso(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error('Invalid lifecycle datetime value');
  }

  return parsed.toISOString();
}

function lifecycleStatus(link: LinkItem): 'active' | 'scheduled' | 'expired' {
  const now = Date.now();
  const fromTime = link.activeFrom ? new Date(link.activeFrom).getTime() : null;
  const untilTime = link.activeUntil ? new Date(link.activeUntil).getTime() : null;

  if (fromTime && !Number.isNaN(fromTime) && now < fromTime) {
    return 'scheduled';
  }

  if (untilTime && !Number.isNaN(untilTime) && now > untilTime) {
    return 'expired';
  }

  return 'active';
}

export default function LinksPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const {
    selectedTenant,
    selectedTenantId,
    canWriteLinks,
    preferences,
    domains,
    makeShortUrl,
    createLink,
    setNotice,
  } = useAdmin();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [customQueryParams, setCustomQueryParams] = useState('');
  const [activeFromInput, setActiveFromInput] = useState('');
  const [activeUntilInput, setActiveUntilInput] = useState('');
  const [redirectType, setRedirectType] = useState<RedirectType>(preferences.defaultRedirectType);
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [sortMode, setSortMode] = useState<SortMode>('recent');

  const [pageLinks, setPageLinks] = useState<LinkItem[]>([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [reloadSeq, setReloadSeq] = useState(0);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    setRedirectType(preferences.defaultRedirectType);
  }, [preferences.defaultRedirectType]);

  const activeDomains = useMemo(
    () => domains.filter((domain) => domain.status === 'active'),
    [domains],
  );
  const selectedCreateDomain = useMemo(
    () => activeDomains.find((domain) => domain.domainId === selectedDomainId) || null,
    [activeDomains, selectedDomainId],
  );
  const linkPreview = useMemo(() => {
    const code = newCode.trim();
    if (!code) {
      return '';
    }
    return makeShortUrl(code, selectedCreateDomain?.domain);
  }, [makeShortUrl, newCode, selectedCreateDomain]);
  const hasSecurityLifecycleValues = Boolean(newPassword.trim() || activeFromInput || activeUntilInput);
  const hasTrackingValues = Boolean(
    utmSource.trim()
      || utmMedium.trim()
      || utmCampaign.trim()
      || utmTerm.trim()
      || utmContent.trim()
      || customQueryParams.trim(),
  );

  useEffect(() => {
    if (activeDomains.length === 0) {
      setSelectedDomainId('');
      return;
    }

    setSelectedDomainId((current) => {
      if (current && activeDomains.some((domain) => domain.domainId === current)) {
        return current;
      }

      return activeDomains.find((domain) => domain.isDefault)?.domainId || activeDomains[0].domainId;
    });
  }, [activeDomains]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentCursor(null);
    setNextCursor(null);
    setCursorHistory([]);
    setPageNumber(1);
  }, [selectedTenantId, debouncedSearch, filterMode, pageSize, sortMode]);

  const fetchLinksPage = useCallback(async () => {
    if (!selectedTenantId) {
      setPageLinks([]);
      setNextCursor(null);
      return;
    }

    setPageLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tenantId', selectedTenantId);
      params.set('paginated', '1');
      params.set('limit', String(pageSize));
      if (currentCursor) params.set('cursor', currentCursor);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterMode !== 'all') params.set('redirectType', filterMode);
      if (sortMode === 'recent' || sortMode === 'oldest' || sortMode === 'code' || sortMode === 'clicks') params.set('sort', sortMode);

      const response = await fetch(`${API_BASE}/links?${params.toString()}`, { headers: authHeaders });
      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: t('links.errorFetch') }));
        throw new Error(data.error || t('links.errorFetch'));
      }

      const data = await response.json() as PaginatedLinksResponse;
      setPageLinks(Array.isArray(data.links) ? data.links : []);
      setNextCursor(data.pagination?.nextCursor || null);
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : t('links.errorFetch') });
      setPageLinks([]);
      setNextCursor(null);
    } finally {
      setPageLoading(false);
    }
  }, [authHeaders, currentCursor, debouncedSearch, filterMode, pageSize, selectedTenantId, setNotice, sortMode, t]);

  useEffect(() => {
    fetchLinksPage().catch(() => undefined);
  }, [fetchLinksPage, reloadSeq]);

  const visibleLinks = useMemo(() => {
    const next = [...pageLinks];
    next.sort((a, b) => {
      if (sortMode === 'code') return a.urlCode.localeCompare(b.urlCode);
      if (sortMode === 'clicks') return (b.clickCount || 0) - (a.clickCount || 0);
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortMode === 'oldest' ? aTime - bTime : bTime - aTime;
    });
    return next;
  }, [pageLinks, sortMode]);

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice({ type: 'success', message: `${label} ${t('common.copied').toLowerCase()}` });
    } catch {
      setNotice({ type: 'error', message: t('links.copyError', { label }) });
    }
  };

  const resetToFirstPage = () => {
    setCurrentCursor(null);
    setNextCursor(null);
    setCursorHistory([]);
    setPageNumber(1);
  };

  const reloadCurrentPage = () => setReloadSeq((c) => c + 1);

  const buildRedirectQueryParams = () => {
    const next: Record<string, string> = {};
    const setIfPresent = (key: string, value: string) => {
      const trimmed = value.trim();
      if (trimmed) next[key] = trimmed;
    };

    setIfPresent('utm_source', utmSource);
    setIfPresent('utm_medium', utmMedium);
    setIfPresent('utm_campaign', utmCampaign);
    setIfPresent('utm_term', utmTerm);
    setIfPresent('utm_content', utmContent);

    for (const row of customQueryParams.split('\n').map((value) => value.trim()).filter(Boolean)) {
      const separatorIndex = row.indexOf('=');
      if (separatorIndex <= 0) {
        throw new Error('Custom params must use key=value format, one per line');
      }

      const key = row.slice(0, separatorIndex).trim();
      const value = row.slice(separatorIndex + 1).trim();
      if (!key) {
        throw new Error('Custom query param key is required');
      }
      next[key] = value;
    }

    return Object.keys(next).length > 0 ? next : undefined;
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsCreating(true);
      const redirectQueryParams = buildRedirectQueryParams();
      const activeFrom = localDateTimeToIso(activeFromInput);
      const activeUntil = localDateTimeToIso(activeUntilInput);
      const created = await createLink({
        originalUrl: newUrl,
        urlCode: newCode || undefined,
        redirectType,
        domainId: selectedDomainId || undefined,
        password: newPassword || undefined,
        redirectQueryParams,
        activeFrom,
        activeUntil,
      });
      const shortUrl = makeShortUrl(created.urlCode, created.domain);
      setNotice({ type: 'success', message: shortUrl ? t('links.created', { url: shortUrl }) : t('links.created', { url: created.urlCode }) });
      setNewUrl('');
      setNewCode('');
      setNewPassword('');
      setUtmSource('');
      setUtmMedium('');
      setUtmCampaign('');
      setUtmTerm('');
      setUtmContent('');
      setCustomQueryParams('');
      setActiveFromInput('');
      setActiveUntilInput('');
      setCreateDialogOpen(false);
      resetToFirstPage();
      reloadCurrentPage();
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : t('links.errorCreate') });
    } finally {
      setIsCreating(false);
    }
  };

  const goNextPage = () => {
    if (!nextCursor || pageLoading) return;
    setCursorHistory((c) => [...c, currentCursor]);
    setCurrentCursor(nextCursor);
    setPageNumber((c) => c + 1);
  };

  const goPreviousPage = () => {
    if (cursorHistory.length === 0 || pageLoading) return;
    const prev = cursorHistory[cursorHistory.length - 1] ?? null;
    setCursorHistory((c) => c.slice(0, -1));
    setCurrentCursor(prev);
    setPageNumber((c) => Math.max(1, c - 1));
  };

  if (!selectedTenant) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('common.noWorkspace')}</CardTitle>
          <CardDescription>{t('links.noWorkspaceDesc')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
      <Card>
        <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{t('links.cardTitle')}</CardTitle>
            <CardDescription>{t('links.cardDesc')}</CardDescription>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} disabled={!canWriteLinks}>
            <Plus className="mr-2 h-4 w-4" />{t('links.newLink')}
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {!canWriteLinks && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('links.roleNotice', { role: selectedTenant.role })}
          </p>
        )}

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t('links.newLink')}</DialogTitle>
              <DialogDescription>{t('links.cardDesc')}</DialogDescription>
            </DialogHeader>
            <form className="space-y-4" onSubmit={handleCreate}>
              <div className="rounded-lg border bg-muted/20 p-4">
                <div className="mb-2">
                  <p className="text-sm font-medium">{t('links.modalDestinationTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('links.modalDestinationHint')}</p>
                </div>
                <Label className="mb-1">{t('links.modalDestinationLabel')}</Label>
                <Input type="url" aria-label="Original URL" placeholder={t('links.modalDestinationPlaceholder')} value={newUrl} onChange={(e) => setNewUrl(e.target.value)} required />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="mb-1">{t('links.codePlaceholder')}</Label>
                  <Input type="text" aria-label="Custom code" placeholder={t('links.modalCodePlaceholder')} value={newCode} onChange={(e) => setNewCode(e.target.value)} />
                </div>
                <div>
                  <Label className="mb-1">{t('linkDetail.labelRedirect')}</Label>
                  <select aria-label="Redirect type" className={`${selectClassName} w-full`} value={redirectType} onChange={(e) => setRedirectType(e.target.value === '302' ? '302' : '301')}>
                    <option value="301">{t('links.redirect301Label')}</option>
                    <option value="302">{t('links.redirect302Label')}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1">{t('links.domainFallback')}</Label>
                  <select aria-label="Link domain" className={`${selectClassName} w-full`} value={selectedDomainId} onChange={(e) => setSelectedDomainId(e.target.value)} disabled={activeDomains.length === 0}>
                    {activeDomains.length === 0 ? (
                      <option value="">{t('links.domainFallback')}</option>
                    ) : (
                      activeDomains.map((domain) => (
                        <option key={domain.domainId} value={domain.domainId}>{domain.domain}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                {linkPreview ? (
                  <>{t('links.modalPreviewPrefix')} <span className="font-mono text-foreground">{linkPreview}</span></>
                ) : (
                  t('links.modalCodeAutoHint')
                )}
              </div>

              <details className="rounded-lg border p-3">
                <summary className="cursor-pointer select-none text-sm font-medium">
                  {t('links.modalSecurityLifecycleTitle')}
                  {hasSecurityLifecycleValues ? <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{t('links.modalConfigured')}</span> : null}
                </summary>
                <p className="mt-1 text-xs text-muted-foreground">{t('links.modalSecurityLifecycleHint')}</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-1">
                    <Label className="mb-1">{t('links.modalPasswordLabel')}</Label>
                    <Input type="password" aria-label="Link password" placeholder={t('links.modalPasswordPlaceholder')} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                    <p className="mt-2 text-xs text-muted-foreground">{t('links.modalPasswordHint')}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="mb-2 text-sm font-medium">{t('links.modalLifecycleTitle')}</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <Label className="mb-1">{t('links.modalActiveFrom')}</Label>
                        <Input type="datetime-local" aria-label="Active from" value={activeFromInput} onChange={(e) => setActiveFromInput(e.target.value)} />
                      </div>
                      <div>
                        <Label className="mb-1">{t('links.modalActiveUntil')}</Label>
                        <Input type="datetime-local" aria-label="Active until" value={activeUntilInput} onChange={(e) => setActiveUntilInput(e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              </details>

              <details className="rounded-lg border p-3">
                <summary className="cursor-pointer select-none text-sm font-medium">
                  {t('links.modalQueryTitle')}
                  {hasTrackingValues ? <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{t('links.modalConfigured')}</span> : null}
                </summary>
                <p className="mt-1 text-xs text-muted-foreground">{t('links.modalQueryHint')}</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input placeholder="utm_source" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} />
                  <Input placeholder="utm_medium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} />
                  <Input placeholder="utm_campaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
                  <Input placeholder="utm_term" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} />
                  <Input placeholder="utm_content" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} />
                </div>
                <Label className="mb-1 mt-3">{t('links.modalCustomParamsLabel')}</Label>
                <textarea
                  className="min-h-[92px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder={'ref=linkedin\nsource_id=campaign_42'}
                  value={customQueryParams}
                  onChange={(e) => setCustomQueryParams(e.target.value)}
                />
              </details>

              <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={isCreating || !canWriteLinks}>{isCreating ? t('links.creating') : t('links.createLink')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
          <Input type="search" aria-label="Search links" placeholder={t('links.searchPlaceholder')} className="w-full lg:max-w-xs" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <select aria-label="Filter links" className={`${selectClassName} w-full`} value={filterMode} onChange={(e) => setFilterMode(e.target.value as FilterMode)}>
            <option value="all">{t('links.filterAll')}</option>
            <option value="301">{t('links.filter301')}</option>
            <option value="302">{t('links.filter302')}</option>
          </select>
          <select aria-label="Sort links" className={`${selectClassName} w-full`} value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
            <option value="recent">{t('links.sortRecent')}</option>
            <option value="oldest">{t('links.sortOldest')}</option>
            <option value="code">{t('links.sortAlpha')}</option>
            <option value="clicks">{t('links.sortClicks')}</option>
          </select>
        </div>

        {pageLoading ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">{t('links.colCode')}</TableHead>
                <TableHead>{t('links.colShortUrl')}</TableHead>
                <TableHead>{t('links.colDomain')}</TableHead>
                <TableHead>{t('links.colTarget')}</TableHead>
                <TableHead className="w-[80px]">{t('links.colType')}</TableHead>
                <TableHead className="w-[80px] text-right">{t('links.colClicks')}</TableHead>
                <TableHead className="w-[120px]">{t('links.colCreated')}</TableHead>
                <TableHead className="w-[40px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-10 rounded-full" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-4 rounded" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : visibleLinks.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('links.noResults')}</p>
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('links.colCode')}</TableHead>
                    <TableHead>{t('links.colDomain')}</TableHead>
                    <TableHead>{t('links.colTarget')}</TableHead>
                    <TableHead>{t('links.colType')}</TableHead>
                    <TableHead>{t('links.colCreated')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleLinks.map((link) => {
                    const shortUrl = makeShortUrl(link.urlCode, link.domain);

                    return (
                      <TableRow key={`${link.urlCode}:${link.domainId || 'no-domain'}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <code className="text-sm font-mono">{link.urlCode}</code>
                              {link.isPasswordProtected && (
                                <Badge variant="outline" className="h-5 px-1.5"><Lock className="h-3 w-3" /></Badge>
                              )}
                              {lifecycleStatus(link) !== 'active' && (
                                <Badge variant="secondary" className="h-5 px-1.5">{lifecycleStatus(link) === 'scheduled' ? 'Scheduled' : 'Expired'}</Badge>
                              )}
                            </div>
                            {shortUrl && (
                              <span className="text-xs text-muted-foreground">{shortUrl}</span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {link.domain ? (
                            <Badge variant="outline" className="font-normal">{link.domain}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('links.domainUnassigned')}</span>
                          )}
                        </TableCell>

                        <TableCell className="max-w-[300px] truncate">
                          <a href={link.originalUrl} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">{link.originalUrl}</a>
                        </TableCell>

                        <TableCell>
                          <Badge variant={link.redirectType === '301' ? 'secondary' : 'outline'}>{link.redirectType}</Badge>
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground">{formatDate(link.createdAt)}</TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              aria-label={`Copy ${link.urlCode}`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (!shortUrl) {
                                  setNotice({ type: 'error', message: t('links.noDomainCopy') });
                                  return;
                                }
                                copyToClipboard(shortUrl, 'Short URL');
                              }}
                            >
                              <Copy className="mr-1 h-3.5 w-3.5" />
                              {t('links.copy')}
                            </Button>
                            <Button variant="outline" size="sm" asChild aria-label={`Open ${link.urlCode}`}>
                              <a
                                className={cn(!shortUrl && 'opacity-50 pointer-events-none')}
                                href={shortUrl || '#'}
                                onClick={(e) => { if (!shortUrl) e.preventDefault(); }}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                                {t('common.open')}
                              </a>
                            </Button>
                            <Button variant="outline" size="sm" asChild>
                              <Link to={`/links/${link.urlCode}${link.domainId ? `?domainId=${encodeURIComponent(link.domainId)}` : ''}`}>
                                <ArrowRight className="mr-1 h-3.5 w-3.5" />
                                {t('links.detail')}
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {visibleLinks.map((link) => {
                const shortUrl = makeShortUrl(link.urlCode, link.domain);
                return (
                  <div key={`${link.urlCode}:${link.domainId || 'no-domain'}`} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <code className="text-sm font-mono">{link.urlCode}</code>
                          {link.isPasswordProtected && (
                            <Badge variant="outline" className="h-5 px-1.5"><Lock className="h-3 w-3" /></Badge>
                          )}
                          {lifecycleStatus(link) !== 'active' && (
                            <Badge variant="secondary" className="h-5 px-1.5">{lifecycleStatus(link) === 'scheduled' ? 'Scheduled' : 'Expired'}</Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{formatDate(link.createdAt)}</p>
                      </div>
                      <Badge variant={link.redirectType === '301' ? 'secondary' : 'outline'}>{link.redirectType}</Badge>
                    </div>
                    <p className="mt-2 break-all text-sm text-muted-foreground">{link.originalUrl}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{link.domain || t('links.domainUnassigned')}</p>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="px-2"
                        onClick={(e) => {
                          e.preventDefault();
                          if (!shortUrl) {
                            setNotice({ type: 'error', message: t('links.noDomainCopy') });
                            return;
                          }
                          copyToClipboard(shortUrl, 'Short URL');
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" asChild className="px-2">
                        <a
                          className={cn(!shortUrl && 'opacity-50 pointer-events-none')}
                          href={shortUrl || '#'}
                          onClick={(e) => { if (!shortUrl) e.preventDefault(); }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" asChild className="px-2">
                        <Link to={`/links/${link.urlCode}${link.domainId ? `?domainId=${encodeURIComponent(link.domainId)}` : ''}`}>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>{t('common.page', { n: pageNumber })}</span>
            <span>{t('links.rowsCount', { count: visibleLinks.length })}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="page-size-select" className="text-sm">{t('links.perPage')}</Label>
            <select id="page-size-select" className={selectClassName} value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value) || 50)} disabled={pageLoading}>
              {PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={String(o)}>{o}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={goPreviousPage} disabled={pageLoading || cursorHistory.length === 0}>
              <ChevronLeft className="mr-1 h-4 w-4" />{t('common.prev')}
            </Button>
            <Button variant="outline" size="sm" onClick={goNextPage} disabled={pageLoading || !nextCursor}>
              {t('common.next')}<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={reloadCurrentPage} disabled={pageLoading}>
              <RefreshCw className="mr-1 h-4 w-4" />{t('common.refresh')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
